<div class="container-fluid mt-4">
    <!-- Page Header -->
    <div class="row mb-3">
        <div class="col">
            <h2>{{ 'SystemAdmin.settings.title' | translate }}</h2>
            <p class="text-muted">{{ 'SystemAdmin.settings.description' | translate }}</p>
        </div>
    </div>

    <!-- Freshdesk Settings -->
    <fieldset class="form-section">
        <legend class="form-section-legend">
            <i class="bi bi-headset me-2"></i>{{ 'SystemAdmin.settings.freshdeskTitle' | translate }}
        </legend>
        <p class="text-muted mb-3">{{ 'SystemAdmin.settings.freshdeskDescription' | translate }}</p>

        <div *ngIf="freshdeskLoading" class="text-center py-4">
            <div class="spinner-border spinner-border-sm text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
        </div>

        <form *ngIf="!freshdeskLoading" [formGroup]="freshdeskForm">
            <div class="row g-3">
                <!-- API URL -->
                <div class="col-md-6" *ngFor="let field of freshdeskFields">
                    <label class="form-label" [for]="field.key">
                        {{ field.labelKey | translate }}
                        <span *ngIf="field.required" class="text-danger">*</span>
                    </label>
                    <div class="input-group">
                        <input
                            [type]="field.type === 'password' ? 'password' : 'text'"
                            class="form-control"
                            [id]="field.key"
                            [formControlName]="field.key"
                            [placeholder]="field.placeholderKey ? (field.placeholderKey | translate) : ''"
                            [ngClass]="{'is-invalid': isFieldInvalid(freshdeskForm, field.key)}"
                        />
                        <span *ngIf="field.type === 'password'" class="input-group-text">
                            <i class="bi bi-key"></i>
                        </span>
                        <span *ngIf="field.type === 'url'" class="input-group-text">
                            <i class="bi bi-link-45deg"></i>
                        </span>
                    </div>
                    <small *ngIf="field.helpKey" class="text-muted">{{ field.helpKey | translate }}</small>
                    <div *ngIf="isFieldInvalid(freshdeskForm, field.key)" class="invalid-feedback d-block">
                        {{ 'General.required' | translate }}
                    </div>
                </div>
            </div>

            <!-- Actions -->
            <div class="d-flex align-items-center gap-2 mt-3">
                <button
                    type="button"
                    class="btn btn-primary"
                    (click)="saveFreshdeskSettings()"
                    [disabled]="freshdeskSaving || freshdeskForm.invalid"
                >
                    <span *ngIf="freshdeskSaving" class="spinner-border spinner-border-sm me-1" role="status"></span>
                    <i *ngIf="!freshdeskSaving" class="bi bi-save me-1"></i>
                    {{ 'General.save' | translate }}
                </button>

                <button
                    type="button"
                    class="btn btn-outline-secondary"
                    (click)="testFreshdeskConnection()"
                    [disabled]="freshdeskTesting"
                >
                    <span *ngIf="freshdeskTesting" class="spinner-border spinner-border-sm me-1" role="status"></span>
                    <i *ngIf="!freshdeskTesting" class="bi bi-plug me-1"></i>
                    {{ 'SystemAdmin.settings.testConnection' | translate }}
                </button>
            </div>

            <!-- Test Result -->
            <div *ngIf="freshdeskTestResult" class="mt-3">
                <div class="alert" [ngClass]="freshdeskTestResult.success ? 'alert-success' : 'alert-danger'" role="alert">
                    <i [class]="freshdeskTestResult.success ? 'bi bi-check-circle-fill me-2' : 'bi bi-x-circle-fill me-2'"></i>
                    {{ freshdeskTestResult.message }}
                </div>
            </div>
        </form>
    </fieldset>

    <!-- Accounting / Økonomisystem Settings -->
    <fieldset class="form-section mt-4">
        <legend class="form-section-legend">
            <i class="bi bi-calculator me-2"></i>{{ 'SystemAdmin.settings.accountingTitle' | translate }}
        </legend>
        <p class="text-muted mb-3">{{ 'SystemAdmin.settings.accountingDescription' | translate }}</p>

        <div *ngIf="accountingLoading" class="text-center py-4">
            <div class="spinner-border spinner-border-sm text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
        </div>

        <form *ngIf="!accountingLoading" [formGroup]="accountingForm">
            <div class="row g-3">
                <div class="col-md-6" *ngFor="let field of accountingFields">
                    <label class="form-label" [for]="field.key">
                        {{ field.labelKey | translate }}
                        <span *ngIf="field.required" class="text-danger">*</span>
                    </label>
                    <input
                        [type]="field.type === 'number' ? 'number' : 'text'"
                        class="form-control"
                        [id]="field.key"
                        [formControlName]="field.key"
                        [placeholder]="field.placeholderKey ? (field.placeholderKey | translate) : ''"
                        [ngClass]="{'is-invalid': isFieldInvalid(accountingForm, field.key)}"
                    />
                    <small *ngIf="field.helpKey" class="text-muted">{{ field.helpKey | translate }}</small>
                    <div *ngIf="isFieldInvalid(accountingForm, field.key)" class="invalid-feedback d-block">
                        {{ 'General.required' | translate }}
                    </div>
                </div>
            </div>

            <!-- Actions -->
            <div class="d-flex align-items-center gap-2 mt-3">
                <button
                    type="button"
                    class="btn btn-primary"
                    (click)="saveAccountingSettings()"
                    [disabled]="accountingSaving || accountingForm.invalid"
                >
                    <span *ngIf="accountingSaving" class="spinner-border spinner-border-sm me-1" role="status"></span>
                    <i *ngIf="!accountingSaving" class="bi bi-save me-1"></i>
                    {{ 'General.save' | translate }}
                </button>
            </div>
        </form>
    </fieldset>

    <!-- Work Order Settings -->
    <fieldset class="form-section mt-4">
        <legend class="form-section-legend">
            <i class="bi bi-clipboard-data me-2"></i>{{ 'SystemAdmin.settings.workOrderTitle' | translate }}
        </legend>
        <p class="text-muted mb-3">{{ 'SystemAdmin.settings.workOrderDescription' | translate }}</p>

        <div *ngIf="workOrderLoading" class="text-center py-4">
            <div class="spinner-border spinner-border-sm text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
        </div>

        <form *ngIf="!workOrderLoading" [formGroup]="workOrderForm">
            <div class="row g-3">
                <div class="col-md-6" *ngFor="let field of workOrderFields">
                    <label class="form-label" [for]="field.key">
                        {{ field.labelKey | translate }}
                        <span *ngIf="field.required" class="text-danger">*</span>
                    </label>
                    <input
                        [type]="field.type === 'number' ? 'number' : 'text'"
                        class="form-control"
                        [id]="field.key"
                        [formControlName]="field.key"
                        [placeholder]="field.placeholderKey ? (field.placeholderKey | translate) : ''"
                        [ngClass]="{'is-invalid': isFieldInvalid(workOrderForm, field.key)}"
                    />
                    <small *ngIf="field.helpKey" class="text-muted">{{ field.helpKey | translate }}</small>
                    <div *ngIf="isFieldInvalid(workOrderForm, field.key)" class="invalid-feedback d-block">
                        {{ 'General.required' | translate }}
                    </div>
                </div>
            </div>

            <div class="d-flex align-items-center gap-2 mt-3">
                <button
                    type="button"
                    class="btn btn-primary"
                    (click)="saveWorkOrderSettings()"
                    [disabled]="workOrderSaving || workOrderForm.invalid"
                >
                    <span *ngIf="workOrderSaving" class="spinner-border spinner-border-sm me-1" role="status"></span>
                    <i *ngIf="!workOrderSaving" class="bi bi-save me-1"></i>
                    {{ 'General.save' | translate }}
                </button>
            </div>
        </form>
    </fieldset>
</div>
